import { gql } from '@apollo/client/core/index.js';

/* ── Service Orders ── */

export const GET_SERVICE_ORDERS = gql`
  query GetServiceOrders(
    $first: Int
    $after: String
    $status: String
    $clientCode: String
    $assignedTo: String
  ) {
    serviceOrders(
      first: $first
      after: $after
      status: $status
      clientCode: $clientCode
      assignedTo: $assignedTo
    ) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          accessKey
          ticketno
          description
          status
          priority
          assignedTo
          clientCode
          createdDate
          dueDate
          completedDate
          jobSite
          jobType
          scheduled
          client {
            clientCode
            clientName
            city
            state
          }
        }
      }
    }
  }
`;

export const GET_SERVICE_ORDER = gql`
  query GetServiceOrder($accessKey: Float!) {
    serviceOrder(accessKey: $accessKey) {
      accessKey
      linkedAk
      ticketno
      description
      notes
      status
      priority
      assignedTo
      clientCode
      createdDate
      dueDate
      completedDate
      jobSite
      jobType
      manpower
      duration
      defaultCrew
      scheduled
      client {
        clientCode
        clientName
        contact
        address
        city
        state
        zip
        phone
        email
      }
      checklistTemplate {
        id
        name
        jobType
      }
    }
  }
`;

/* ── Service Order Units (paginated) ── */

export const GET_SERVICE_ORDER_UNITS = gql`
  query GetServiceOrderUnits(
    $first: Int
    $after: String
    $serviceOrderAccessKey: Float
    $floor: String
    $serviceComplete: Int
  ) {
    serviceOrderUnits(
      first: $first
      after: $after
      serviceOrderAccessKey: $serviceOrderAccessKey
      floor: $floor
      serviceComplete: $serviceComplete
    ) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          accessKey
          unit
          unitnumber
          floor
          location
          serviceComplete
          accessDenied
          servicetech
          comments
        }
      }
    }
  }
`;

/* ── Unit Summaries (grouped, efficient) ── */

export const GET_UNIT_SUMMARIES = gql`
  query GetUnitSummaries($serviceOrderAccessKey: Float!) {
    unitSummaries(serviceOrderAccessKey: $serviceOrderAccessKey) {
      floor
      unit
      locationCount
      completedCount
      deniedCount
    }
  }
`;

export const GET_UNIT_LOCATIONS = gql`
  query GetUnitLocations($serviceOrderAccessKey: Float!, $unit: String!, $floor: String!) {
    unitLocations(serviceOrderAccessKey: $serviceOrderAccessKey, unit: $unit, floor: $floor) {
      accessKey
      location
      serviceComplete
      accessDenied
      servicetech
      comments
    }
  }
`;

/* ── Clients ── */

export const GET_CLIENTS = gql`
  query GetClients($first: Int, $after: String) {
    clients(first: $first, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          clientCode
          clientName
          contact
          city
          state
          phone
          email
          status
          type
          inactive
          onHold
        }
      }
    }
  }
`;

/* ── Users ── */

export const GET_USERS = gql`
  query GetUsers($first: Int, $after: String) {
    users(first: $first, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          firstName
          lastName
          email
          username
          groupId
          userType
          active
          lastLogin
        }
      }
    }
  }
`;

/* ── Accounts ── */

export const GET_ACCOUNTS = gql`
  query GetAccounts($first: Int, $after: String) {
    accounts(first: $first, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          name
          code
          active
        }
      }
    }
  }
`;

/* ── Time Locations ── */

export const GET_TIME_LOCATIONS = gql`
  query GetTimeLocations($first: Int, $after: String, $dateFrom: String, $dateTo: String) {
    timeLocations(first: $first, after: $after, dateFrom: $dateFrom, dateTo: $dateTo) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          accessKey
          user
          clockInTime
          clockOutTime
          clockInLatitude
          clockInLongitude
          clockOutLatitude
          clockOutLongitude
          clockInAddress
          clockOutAddress
          linkedOrderAk
        }
      }
    }
  }
`;

/* ── Contracts & Checklists ── */

export const GET_CONTRACTS = gql`
  query GetContracts($first: Int, $after: String) {
    contracts(first: $first, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          accessKey
          contractNumber
          contractType
          serviceLevel
          serviceOrderAk
          checklists {
            accessKey
            linkedAk
            location
            seq
            item
            mobileFieldType
            displayList
            group
            showWhen
          }
        }
      }
    }
  }
`;

/* ── Mutations ── */

export const CREATE_SERVICE_ORDER = gql`
  mutation CreateServiceOrder(
    $accessKey: Float!
    $clientCode: String
    $ticketno: String
    $description: String
    $notes: String
    $status: String
    $priority: Int
    $assignedTo: String
    $dueDate: String
  ) {
    createServiceOrder(
      accessKey: $accessKey
      clientCode: $clientCode
      ticketno: $ticketno
      description: $description
      notes: $notes
      status: $status
      priority: $priority
      assignedTo: $assignedTo
      dueDate: $dueDate
    ) {
      accessKey
      ticketno
      status
    }
  }
`;

export const UPDATE_SERVICE_ORDER = gql`
  mutation UpdateServiceOrder(
    $accessKey: Float!
    $clientCode: String
    $ticketno: String
    $description: String
    $notes: String
    $status: String
    $priority: Int
    $assignedTo: String
    $dueDate: String
    $completedDate: String
  ) {
    updateServiceOrder(
      accessKey: $accessKey
      clientCode: $clientCode
      ticketno: $ticketno
      description: $description
      notes: $notes
      status: $status
      priority: $priority
      assignedTo: $assignedTo
      dueDate: $dueDate
      completedDate: $completedDate
    ) {
      accessKey
      status
    }
  }
`;

export const CREATE_CLIENT = gql`
  mutation CreateClient(
    $clientCode: String!
    $clientName: String
    $contact: String
    $address: String
    $city: String
    $state: String
    $zip: String
    $phone: String
    $fax: String
    $email: String
    $notes: String
  ) {
    createClient(
      clientCode: $clientCode
      clientName: $clientName
      contact: $contact
      address: $address
      city: $city
      state: $state
      zip: $zip
      phone: $phone
      fax: $fax
      email: $email
      notes: $notes
    ) {
      clientCode
      clientName
    }
  }
`;

export const UPDATE_CLIENT = gql`
  mutation UpdateClient(
    $clientCode: String!
    $clientName: String
    $contact: String
    $address: String
    $city: String
    $state: String
    $zip: String
    $phone: String
    $fax: String
    $email: String
    $notes: String
  ) {
    updateClient(
      clientCode: $clientCode
      clientName: $clientName
      contact: $contact
      address: $address
      city: $city
      state: $state
      zip: $zip
      phone: $phone
      fax: $fax
      email: $email
      notes: $notes
    ) {
      clientCode
      clientName
    }
  }
`;

export const CREATE_USER = gql`
  mutation CreateUser(
    $username: String!
    $passwordHash: String!
    $passwordSalt: String!
    $accountId: Int!
    $firstName: String
    $lastName: String
    $email: String
    $groupId: Int
    $userType: String
  ) {
    createUser(
      username: $username
      passwordHash: $passwordHash
      passwordSalt: $passwordSalt
      accountId: $accountId
      firstName: $firstName
      lastName: $lastName
      email: $email
      groupId: $groupId
      userType: $userType
    ) {
      id
      firstName
      lastName
      email
    }
  }
`;

export const UPDATE_USER = gql`
  mutation UpdateUser(
    $id: Int!
    $firstName: String
    $lastName: String
    $email: String
    $groupId: Int
    $userType: String
    $active: Int
    $screens: [String!]
  ) {
    updateUser(
      id: $id
      firstName: $firstName
      lastName: $lastName
      email: $email
      groupId: $groupId
      userType: $userType
      active: $active
      screens: $screens
    ) {
      id
      firstName
      lastName
      active
    }
  }
`;

/* ── Single User ── */

export const GET_USER = gql`
  query GetUser($id: Int!) {
    user(id: $id) {
      id
      username
      firstName
      lastName
      email
      groupId
      userType
      active
      lastLogin
    }
  }
`;

/* ── Units ── */

export const GET_UNITS = gql`
  query GetUnits(
    $first: Int
    $after: String
    $clientCode: String
    $floor: String
    $serviceComplete: Int
    $accessDenied: Int
  ) {
    serviceOrderUnits(
      first: $first
      after: $after
      clientCode: $clientCode
      floor: $floor
      serviceComplete: $serviceComplete
      accessDenied: $accessDenied
    ) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          accessKey
          clientCode
          linkedAk
          unit
          unitnumber
          location
          floor
          ownerTenant
          equipment
          serviceDate
          serviceComplete
          accessDenied
          lastUpdated
          serviceOrder {
            accessKey
            ticketno
            client {
              clientName
            }
          }
        }
      }
    }
  }
`;

export const GET_UNIT = gql`
  query GetUnit($accessKey: Float!) {
    serviceOrderUnit(accessKey: $accessKey) {
      accessKey
      clientCode
      linkedAk
      unit
      unitnumber
      location
      floor
      ownerTenant
      equipment
      serviceDate
      serviceComplete
      accessDenied
      lastUpdated
      servicetech
      comments
      servicetime
      checklistItems
      propertyId
      property {
        id
        name
        clientCode
      }
      locations {
        id
        unitAk
        name
        description
        seq
      }
      serviceOrder {
        accessKey
        ticketno
        description
        client {
          clientName
          clientCode
        }
      }
    }
  }
`;

export const UPDATE_UNIT = gql`
  mutation UpdateUnit(
    $accessKey: Float!
    $unit: String
    $unitnumber: String
    $location: String
    $floor: String
    $ownerTenant: String
    $equipment: String
    $serviceDate: String
    $serviceComplete: Int
    $accessDenied: Int
    $notes: String
  ) {
    updateServiceOrderUnit(
      accessKey: $accessKey
      unit: $unit
      unitnumber: $unitnumber
      location: $location
      floor: $floor
      ownerTenant: $ownerTenant
      equipment: $equipment
      serviceDate: $serviceDate
      serviceComplete: $serviceComplete
      accessDenied: $accessDenied
      notes: $notes
    ) {
      accessKey
      serviceComplete
      accessDenied
    }
  }
`;

/* ── Checklist mutations ── */

export const CREATE_CHECKLIST = gql`
  mutation CreateChecklist(
    $accessKey: Float!
    $linkedAk: Float!
    $location: String!
    $seq: Int!
    $item: String!
    $mobileFieldType: String!
    $displayList: String
    $group: String
    $showWhen: String
    $linked: Int
  ) {
    createChecklist(
      accessKey: $accessKey
      linkedAk: $linkedAk
      location: $location
      seq: $seq
      item: $item
      mobileFieldType: $mobileFieldType
      displayList: $displayList
      group: $group
      showWhen: $showWhen
      linked: $linked
    ) {
      accessKey
      item
      mobileFieldType
      seq
    }
  }
`;

export const UPDATE_CHECKLIST = gql`
  mutation UpdateChecklist(
    $accessKey: Float!
    $location: String
    $seq: Int
    $item: String
    $mobileFieldType: String
    $displayList: String
    $group: String
    $showWhen: String
    $linked: Int
  ) {
    updateChecklist(
      accessKey: $accessKey
      location: $location
      seq: $seq
      item: $item
      mobileFieldType: $mobileFieldType
      displayList: $displayList
      group: $group
      showWhen: $showWhen
      linked: $linked
    ) {
      accessKey
      item
      mobileFieldType
      seq
    }
  }
`;

export const DELETE_CHECKLIST = gql`
  mutation DeleteChecklist($accessKey: Float!) {
    deleteChecklist(accessKey: $accessKey)
  }
`;

/* ── Screens & Groups ── */

export const GET_SCREENS = gql`
  query GetScreens {
    screens {
      id
      name
      parentId
      forbidden
    }
  }
`;

export const GET_USER_SCREENS = gql`
  query GetUserScreens($userId: Int!) {
    userScreens(userId: $userId) {
      id
      screenId
      screenName
      level
    }
  }
`;

export const GET_GROUPS = gql`
  query GetGroups {
    groups {
      id
      name
      accountId
    }
  }
`;

/* ── Delete mutations ── */

export const DELETE_CLIENT = gql`
  mutation DeleteClient($clientCode: String!) {
    deleteClient(clientCode: $clientCode)
  }
`;

export const DELETE_SERVICE_ORDER = gql`
  mutation DeleteServiceOrder($accessKey: Float!) {
    deleteServiceOrder(accessKey: $accessKey)
  }
`;

export const DELETE_USER = gql`
  mutation DeleteUser($id: Int!) {
    deleteUser(id: $id)
  }
`;

/* ── Dashboard ── */

export const GET_DASHBOARD_STATS = gql`
  query GetDashboardStats($dateFrom: String, $dateTo: String) {
    dashboardStats(dateFrom: $dateFrom, dateTo: $dateTo) {
      activeOrders
      completedToday
      totalOrders
      avgCompletionHours
      activeTechnicians
      pendingOrders
      overdueOrders
    }
  }
`;

export const GET_RECENT_ACTIVITY = gql`
  query GetRecentActivity($limit: Int) {
    recentActivity(limit: $limit) {
      id
      type
      message
      timestamp
      user
    }
  }
`;

export const GET_ACTIVE_TECHNICIAN_LOCATIONS = gql`
  query GetActiveTechnicianLocations {
    timeLocations(first: 50) {
      edges {
        node {
          accessKey
          user
          clockInTime
          clockOutTime
          clockInLatitude
          clockInLongitude
          clockInAddress
          clockOutLatitude
          clockOutLongitude
          clockOutAddress
        }
      }
    }
  }
`;

/* ── Properties ── */

export const GET_PROPERTIES = gql`
  query GetProperties($first: Int, $after: String, $clientCode: String, $active: Int) {
    properties(first: $first, after: $after, clientCode: $clientCode, active: $active) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          clientCode
          name
          address
          city
          province
          postalCode
          type
          totalUnits
          active
          client {
            clientCode
            clientName
          }
        }
      }
    }
  }
`;

export const GET_PROPERTY = gql`
  query GetProperty($id: Int!) {
    property(id: $id) {
      id
      clientCode
      name
      address
      city
      province
      postalCode
      type
      totalUnits
      notes
      active
      enteredBy
      enteredDate
      updatedBy
      updatedDate
      client {
        clientCode
        clientName
      }
      floors {
        id
        name
        seq
        units {
          id
          name
          unitType
          seq
          locationTemplates {
            id
            name
            seq
          }
        }
      }
    }
  }
`;

export const CREATE_PROPERTY = gql`
  mutation CreateProperty(
    $clientCode: String!
    $name: String!
    $address: String
    $city: String
    $province: String
    $postalCode: String
    $type: String
    $totalUnits: Int
    $notes: String
  ) {
    createProperty(
      clientCode: $clientCode
      name: $name
      address: $address
      city: $city
      province: $province
      postalCode: $postalCode
      type: $type
      totalUnits: $totalUnits
      notes: $notes
    ) {
      id
      name
      clientCode
    }
  }
`;

export const UPDATE_PROPERTY = gql`
  mutation UpdateProperty(
    $id: Int!
    $clientCode: String
    $name: String
    $address: String
    $city: String
    $province: String
    $postalCode: String
    $type: String
    $totalUnits: Int
    $notes: String
    $active: Int
  ) {
    updateProperty(
      id: $id
      clientCode: $clientCode
      name: $name
      address: $address
      city: $city
      province: $province
      postalCode: $postalCode
      type: $type
      totalUnits: $totalUnits
      notes: $notes
      active: $active
    ) {
      id
      name
      clientCode
    }
  }
`;

export const DELETE_PROPERTY = gql`
  mutation DeleteProperty($id: Int!) {
    deleteProperty(id: $id)
  }
`;

/* ── Locations ── */

export const GET_LOCATIONS = gql`
  query GetLocations($first: Int, $after: String, $unitAk: Float) {
    locations(first: $first, after: $after, unitAk: $unitAk) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          unitAk
          name
          description
          seq
        }
      }
    }
  }
`;

export const CREATE_LOCATION = gql`
  mutation CreateLocation($unitAk: Float!, $name: String!, $description: String, $seq: Int) {
    createLocation(unitAk: $unitAk, name: $name, description: $description, seq: $seq) {
      id
      name
      seq
    }
  }
`;

export const UPDATE_LOCATION = gql`
  mutation UpdateLocation($id: Int!, $name: String, $description: String, $seq: Int) {
    updateLocation(id: $id, name: $name, description: $description, seq: $seq) {
      id
      name
      seq
    }
  }
`;

export const DELETE_LOCATION = gql`
  mutation DeleteLocation($id: Int!) {
    deleteLocation(id: $id)
  }
`;

/* ── Checklist Templates ── */

export const GET_CHECKLIST_TEMPLATES = gql`
  query GetChecklistTemplates($first: Int, $after: String, $jobType: String, $active: Int) {
    checklistTemplates(first: $first, after: $after, jobType: $jobType, active: $active) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          name
          description
          jobType
          version
          active
          fields {
            id
            seq
            fieldName
            fieldType
            required
            group
          }
        }
      }
    }
  }
`;

export const GET_CHECKLIST_TEMPLATE = gql`
  query GetChecklistTemplate($id: Int!) {
    checklistTemplate(id: $id) {
      id
      name
      description
      jobType
      version
      active
      enteredBy
      enteredDate
      updatedBy
      updatedDate
      fields {
        id
        templateId
        seq
        fieldName
        fieldType
        required
        group
        options
        showWhen
      }
    }
  }
`;

export const CREATE_CHECKLIST_TEMPLATE = gql`
  mutation CreateChecklistTemplate($name: String!, $description: String, $jobType: String) {
    createChecklistTemplate(name: $name, description: $description, jobType: $jobType) {
      id
      name
      jobType
    }
  }
`;

export const UPDATE_CHECKLIST_TEMPLATE = gql`
  mutation UpdateChecklistTemplate(
    $id: Int!
    $name: String
    $description: String
    $jobType: String
    $active: Int
  ) {
    updateChecklistTemplate(
      id: $id
      name: $name
      description: $description
      jobType: $jobType
      active: $active
    ) {
      id
      name
      jobType
      active
    }
  }
`;

export const DELETE_CHECKLIST_TEMPLATE = gql`
  mutation DeleteChecklistTemplate($id: Int!) {
    deleteChecklistTemplate(id: $id)
  }
`;

export const CREATE_CHECKLIST_TEMPLATE_FIELD = gql`
  mutation CreateChecklistTemplateField(
    $templateId: Int!
    $seq: Int!
    $fieldName: String!
    $fieldType: String!
    $required: Int
    $group: String
    $options: String
    $showWhen: String
  ) {
    createChecklistTemplateField(
      templateId: $templateId
      seq: $seq
      fieldName: $fieldName
      fieldType: $fieldType
      required: $required
      group: $group
      options: $options
      showWhen: $showWhen
    ) {
      id
      seq
      fieldName
      fieldType
    }
  }
`;

export const UPDATE_CHECKLIST_TEMPLATE_FIELD = gql`
  mutation UpdateChecklistTemplateField(
    $id: Int!
    $seq: Int
    $fieldName: String
    $fieldType: String
    $required: Int
    $group: String
    $options: String
    $showWhen: String
  ) {
    updateChecklistTemplateField(
      id: $id
      seq: $seq
      fieldName: $fieldName
      fieldType: $fieldType
      required: $required
      group: $group
      options: $options
      showWhen: $showWhen
    ) {
      id
      seq
      fieldName
      fieldType
    }
  }
`;

export const DELETE_CHECKLIST_TEMPLATE_FIELD = gql`
  mutation DeleteChecklistTemplateField($id: Int!) {
    deleteChecklistTemplateField(id: $id)
  }
`;

/* ── Unit Contacts ── */

export const GET_UNIT_CONTACTS = gql`
  query GetUnitContacts($first: Int, $clientCode: String, $propertyId: Int, $contactType: String) {
    unitContacts(
      first: $first
      clientCode: $clientCode
      propertyId: $propertyId
      contactType: $contactType
    ) {
      totalCount
      edges {
        node {
          id
          firstName
          lastName
          email
          phone
          contactType
          propertyId
          unitAk
          active
          property {
            id
            name
          }
        }
      }
    }
  }
`;

export const GET_PROPERTIES_FOR_SELECT = gql`
  query GetPropertiesForSelect($first: Int) {
    properties(first: $first) {
      edges {
        node {
          id
          name
          clientCode
        }
      }
    }
  }
`;

export const CREATE_UNIT_CONTACT = gql`
  mutation CreateUnitContact(
    $clientCode: String!
    $propertyId: Int!
    $unitAk: Float
    $contactType: String!
    $firstName: String!
    $lastName: String!
    $email: String
    $phone: String
  ) {
    createUnitContact(
      clientCode: $clientCode
      propertyId: $propertyId
      unitAk: $unitAk
      contactType: $contactType
      firstName: $firstName
      lastName: $lastName
      email: $email
      phone: $phone
    ) {
      id
      firstName
      lastName
    }
  }
`;

export const UPDATE_UNIT_CONTACT = gql`
  mutation UpdateUnitContact(
    $id: Int!
    $firstName: String
    $lastName: String
    $email: String
    $phone: String
    $contactType: String
    $active: Int
  ) {
    updateUnitContact(
      id: $id
      firstName: $firstName
      lastName: $lastName
      email: $email
      phone: $phone
      contactType: $contactType
      active: $active
    ) {
      id
      firstName
      lastName
    }
  }
`;

export const DELETE_UNIT_CONTACT = gql`
  mutation DeleteUnitContact($id: Int!) {
    deleteUnitContact(id: $id)
  }
`;

/* ── Service Requests ── */

export const GET_SERVICE_REQUESTS = gql`
  query GetServiceRequests($first: Int, $status: String, $clientCode: String) {
    serviceRequests(first: $first, status: $status, clientCode: $clientCode) {
      totalCount
      edges {
        node {
          id
          clientCode
          propertyId
          unitAk
          contactId
          description
          urgency
          status
          serviceOrderAk
          createdAt
          updatedAt
          contact {
            id
            firstName
            lastName
            email
            phone
          }
          property {
            id
            name
            address
            city
            province
          }
        }
      }
    }
  }
`;

export const UPDATE_SERVICE_REQUEST_STATUS = gql`
  mutation UpdateServiceRequestStatus($id: Int!, $status: String!, $serviceOrderAk: Float) {
    updateServiceRequestStatus(id: $id, status: $status, serviceOrderAk: $serviceOrderAk) {
      id
      status
      serviceOrderAk
    }
  }
`;

/* ── Activity Timeline & Performance ── */

export const GET_SERVICE_ORDER_TIMELINE = gql`
  query GetServiceOrderTimeline($serviceOrderAk: Float!, $limit: Int) {
    serviceOrderTimeline(serviceOrderAk: $serviceOrderAk, limit: $limit) {
      id
      timestamp
      eventType
      description
      performedBy
      unitLabel
      durationMinutes
      location
      previousValue
      newValue
    }
  }
`;

export const GET_SERVICE_ORDER_PERFORMANCE = gql`
  query GetServiceOrderPerformance($serviceOrderAk: Float!) {
    serviceOrderPerformance(serviceOrderAk: $serviceOrderAk) {
      totalUnits
      completedUnits
      accessDeniedUnits
      totalTimeHours
      estimatedDurationHours
      averageMinutesPerUnit
      technicianBreakdown {
        technician
        unitsCompleted
        totalHours
        averageMinutesPerUnit
      }
      timeDistribution {
        unitLabel
        startTime
        endTime
        durationMinutes
        technician
      }
    }
  }
`;
